import { useEffect, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Lock, Loader2, RefreshCw, Download, TrendingUp, Users, Tag,
  CheckCircle2, HelpCircle, AlertTriangle, History, Info,
} from "lucide-react";

// Historical Replay page. Strictly read-only, never writes
// to allocations / shifts / leaves / swaps / draft_allocations. The page
// answers "if NextRota had been driving Coverage + Assignment in this past
// window, how would it have done?" Trust score is weighted:
// (would_resolve × 1.0 + matched_manager × 0.5) / total_classified

const OUTCOME_LABELS = {
  would_resolve:   "Would have resolved",
  matched_manager: "Matched manager",
  would_conflict:  "Different from manager",
  needed_manager:  "Manager Review Required",
};
const OUTCOME_DESCRIPTIONS = {
  would_resolve:   "Strong-fit candidate available, engine would have closed this gap on its own.",
  matched_manager: "Engine's top pick was one of the people you actually used. Useful corroboration.",
  would_conflict:  "Engine had a strong recommendation, but with watch-outs or a different person, you might have rejected it.",
  needed_manager:  "Engine had no strong-fit candidate. Manager judgement is the right call here.",
};
const OUTCOME_COLOURS = {
  would_resolve:   { ring: "border-emerald-300", chip: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  matched_manager: { ring: "border-blue-300",    chip: "bg-blue-100 text-blue-800 border-blue-200" },
  would_conflict:  { ring: "border-amber-300",   chip: "bg-amber-100 text-amber-800 border-amber-200" },
  needed_manager:  { ring: "border-slate-300",   chip: "bg-slate-100 text-slate-700 border-slate-200" },
};

function defaultWindow() {
  const today = new Date();
  const to = new Date(today); to.setDate(to.getDate() - 1);          // yesterday
  const from = new Date(today); from.setDate(from.getDate() - 30);   // last 30 days
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(from), to: fmt(to) };
}

export default function HistoricalReplayPage() {
  const [window, setWindow] = useState(defaultWindow());
  const [running, setRunning] = useState(false);
  const [replay, setReplay] = useState(null);
  const [outcomeFilter, setOutcomeFilter] = useState(null);
  const [rows, setRows] = useState(null);

  useEffect(() => {
    // On first load, fetch the most recent existing replay for this tenant.
    (async () => {
      try {
        const { data } = await api.get("/replays?limit=1");
        if (data?.items?.length) setReplay(data.items[0]);
      } catch { /* silent */ }
    })();
  }, []);

  const run = useCallback(async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/replays/run", {
        from_date: window.from, to_date: window.to,
      });
      setReplay(data);
      setRows(null);
      setOutcomeFilter(null);
      toast.success("Historical Replay complete, read-only analysis. No live data was changed.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(formatError(detail) || "Failed to run Historical Replay");
    } finally { setRunning(false); }
  }, [window]);

  const loadRows = useCallback(async (outcome) => {
    if (!replay?.replay_id) return;
    setOutcomeFilter(outcome);
    try {
      const q = outcome ? `?outcome=${outcome}&limit=500` : "?limit=500";
      const { data } = await api.get(`/replays/${replay.replay_id}/rows${q}`);
      setRows(data.items || []);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to load rows");
    }
  }, [replay?.replay_id]);

  async function downloadCsv() {
    if (!replay?.replay_id) return;
    try {
      const res = await api.get(`/replays/${replay.replay_id}/export.csv`,
                                  { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `historical-replay-${replay.replay_id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("CSV download failed");
    }
  }

  const rs = replay?.result_summary || {};

  return (
    <div data-testid="historical-replay-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyebrow flex items-center gap-2">
            Automation
            <span className="text-[10px] uppercase tracking-widest text-blue-800 px-1.5 py-0.5 rounded bg-blue-100 border border-blue-200">
              Read-only · No live data changes
            </span>
          </div>
          <h1 className="font-display text-4xl mt-1 flex items-center gap-2">
            <History className="h-7 w-7 text-[#2563EB]" />
            Historical Performance
          </h1>
          <p className="text-sm text-[#4B5773] mt-2 max-w-2xl">
            Replay a past period and see what NextRota would have done. Use this to build trust in the engine&apos;s judgement before letting it drive more decisions. This page never writes to your live rota.
          </p>
        </div>
      </div>

      {/* Window picker */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 mb-5">
        <div className="flex flex-wrap items-end gap-3" data-testid="replay-window-picker">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-[#64748B] block">From</label>
            <input type="date" value={window.from} max={window.to}
                   onChange={(e) => setWindow({ ...window, from: e.target.value })}
                   className="border border-slate-300 rounded px-2 py-1 text-sm"
                   data-testid="replay-from" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-[#64748B] block">To</label>
            <input type="date" value={window.to}
                   onChange={(e) => setWindow({ ...window, to: e.target.value })}
                   className="border border-slate-300 rounded px-2 py-1 text-sm"
                   data-testid="replay-to" />
          </div>
          <div className="text-xs text-[#64748B] ml-2 mb-1">
            Past window only · capped at 90 days
          </div>
          <div className="ml-auto flex gap-2">
            <Button size="sm" onClick={run} disabled={running}
                    className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                    data-testid="replay-run-btn">
              {running ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <TrendingUp className="h-3.5 w-3.5 mr-1" />}
              Run Historical Replay
            </Button>
            {replay && (
              <Button size="sm" variant="outline" onClick={downloadCsv}
                      data-testid="replay-csv-btn">
                <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
              </Button>
            )}
          </div>
        </div>
      </div>

      {!replay ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
          <History className="h-8 w-8 text-slate-400 mx-auto mb-2" />
          <p className="text-sm text-[#4B5773]">No replays yet. Pick a past window and click <strong>Run Historical Replay</strong>.</p>
        </div>
      ) : (
        <>
          {/* Headline trust card, explanation dominates the badge */}
          <div className="rounded-lg border-2 border-[#2563EB]/30 bg-gradient-to-br from-[#F6F9FE] to-white p-5 mb-5"
               data-testid="replay-headline">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <p className="text-sm text-[#4B5773] mb-1">Over this past period</p>
                <p className="text-2xl text-[#0B1A36] leading-snug">
                  NextRota would have correctly addressed{" "}
                  <strong className="text-[#1D4ED8]">{rs.engine_would_have_resolved_count || 0}</strong>{" "}
                  of <strong>{rs.total_classified || 0}</strong> historical coverage decisions
                  {(rs.engine_match_with_manager_count || 0) > 0 && (
                    <> and matched your call on another <strong>{rs.engine_match_with_manager_count}</strong></>
                  )}.
                </p>
                <p className="text-[11px] text-[#64748B] mt-2">
                  Trust score = (would have resolved × 1.0 + matched manager × 0.5) ÷ total decisions
                </p>
              </div>
              <div className="text-right">
                <div className="text-5xl font-medium text-[#1D4ED8]" data-testid="replay-trust-score">
                  {rs.trust_score_pct ?? 0}%
                </div>
                <p className="text-xs uppercase tracking-widest text-[#64748B] mt-1">Trust score</p>
              </div>
            </div>
          </div>

          {/* 4 outcome counter cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="replay-outcome-cards">
            <OutcomeCard outcome="would_resolve" count={rs.engine_would_have_resolved_count} onClick={() => loadRows("would_resolve")} active={outcomeFilter === "would_resolve"} />
            <OutcomeCard outcome="matched_manager" count={rs.engine_match_with_manager_count} onClick={() => loadRows("matched_manager")} active={outcomeFilter === "matched_manager"} />
            <OutcomeCard outcome="would_conflict" count={rs.engine_would_have_been_rejected_count} onClick={() => loadRows("would_conflict")} active={outcomeFilter === "would_conflict"} />
            <OutcomeCard outcome="needed_manager" count={rs.engine_would_have_needed_manager_count} onClick={() => loadRows("needed_manager")} active={outcomeFilter === "needed_manager"} />
          </div>

          {/* Insights */}
          {(replay.insights || []).length > 0 && (
            <div className="rounded-lg border border-[#DBE7FE] bg-[#F6F9FE] p-4 mb-5" data-testid="replay-insights">
              <div className="flex items-center gap-2 mb-2">
                <Info className="h-4 w-4 text-[#2563EB]" />
                <h3 className="font-medium text-sm text-[#0B1A36]">What this tells you</h3>
                <span className="text-[10px] uppercase tracking-widest text-blue-800 px-1.5 py-0.5 rounded bg-blue-100 border border-blue-200 ml-auto">
                  Informational
                </span>
              </div>
              <ul className="space-y-1.5">
                {replay.insights.map((i, idx) => (
                  <li key={idx} className="text-sm text-[#0B1A36] flex items-start gap-2">
                    <span className="text-[#2563EB] mt-1">·</span><span>{i}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Confidence distribution */}
          {rs.confidence_distribution && (
            <ConfidenceDistribution dist={rs.confidence_distribution} />
          )}

          {/* By team + by category */}
          <div className="grid lg:grid-cols-2 gap-4 mb-5">
            <BreakdownTable title="By team" icon={Users} rows={rs.by_team || []} testid="replay-by-team" />
            <BreakdownTable title="By category" icon={Tag} rows={rs.by_category || []} testid="replay-by-category" />
          </div>

          {/* Per-gap detail */}
          <div className="rounded-lg border border-slate-200 bg-white" data-testid="replay-rows-section">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
              <h3 className="font-medium text-sm text-[#0B1A36]">Per-decision detail</h3>
              {outcomeFilter && (
                <span className="text-xs text-[#4B5773]">
                  filtered to <strong>{OUTCOME_LABELS[outcomeFilter]}</strong>
                </span>
              )}
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={() => loadRows(null)}
                        data-testid="replay-rows-load-all">
                  <RefreshCw className="h-3 w-3 mr-1" /> Show all
                </Button>
              </div>
            </div>
            {rows === null ? (
              <p className="px-4 py-6 text-sm text-[#4B5773]">Click a counter card above or "Show all" to load the per-decision rows.</p>
            ) : rows.length === 0 ? (
              <p className="px-4 py-6 text-sm text-[#4B5773]">No rows in this category.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {rows.map((r, i) => <ReplayRow key={i} row={r} />)}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function OutcomeCard({ outcome, count, onClick, active }) {
  const c = OUTCOME_COLOURS[outcome];
  return (
    <button type="button" onClick={onClick}
            className={`text-left rounded-lg border-2 ${c.ring} bg-white p-3 hover:shadow transition ${active ? "ring-2 ring-[#2563EB]/40" : ""}`}
            data-testid={`replay-outcome-card-${outcome}`}>
      <div className="text-[11px] uppercase tracking-widest text-[#64748B]">{OUTCOME_LABELS[outcome]}</div>
      <div className="text-2xl font-medium text-[#0B1A36] mt-1">{count ?? 0}</div>
      <p className="text-[11px] text-[#4B5773] mt-1 leading-snug">{OUTCOME_DESCRIPTIONS[outcome]}</p>
    </button>
  );
}

function ConfidenceDistribution({ dist }) {
  const total = (dist.strong || 0) + (dist.good || 0) + (dist.fair || 0) + (dist.no_candidate || 0);
  if (total === 0) return null;
  const pct = (n) => total ? Math.round(100 * n / total) : 0;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 mb-5" data-testid="replay-confidence-dist">
      <h3 className="font-medium text-sm text-[#0B1A36] mb-2">Confidence distribution</h3>
      <div className="flex h-3 rounded overflow-hidden border border-slate-200">
        <div className="bg-emerald-500" style={{ width: `${pct(dist.strong)}%` }} title={`Strong: ${dist.strong}`} />
        <div className="bg-blue-400" style={{ width: `${pct(dist.good)}%` }} title={`Good: ${dist.good}`} />
        <div className="bg-amber-400" style={{ width: `${pct(dist.fair)}%` }} title={`Fair: ${dist.fair}`} />
        <div className="bg-slate-300" style={{ width: `${pct(dist.no_candidate)}%` }} title={`No candidate: ${dist.no_candidate}`} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
        <Legend swatch="bg-emerald-500" label="Strong" count={dist.strong} pct={pct(dist.strong)} />
        <Legend swatch="bg-blue-400" label="Good" count={dist.good} pct={pct(dist.good)} />
        <Legend swatch="bg-amber-400" label="Fair" count={dist.fair} pct={pct(dist.fair)} />
        <Legend swatch="bg-slate-300" label="No suitable candidate" count={dist.no_candidate} pct={pct(dist.no_candidate)} />
      </div>
      <p className="text-[11px] text-[#64748B] mt-2">Drafts default to strong matches only. Good and fair counts are shown here so you can decide later whether to loosen the threshold.</p>
    </div>
  );
}

function Legend({ swatch, label, count, pct }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-block w-3 h-3 rounded ${swatch}`} />
      <span className="text-[#0B1A36]">{label}</span>
      <span className="text-[#64748B] ml-auto">{count ?? 0} · {pct}%</span>
    </div>
  );
}

function BreakdownTable({ title, icon: Icon, rows, testid }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-[#2563EB]" />
        <h3 className="font-medium text-sm text-[#0B1A36]">{title}</h3>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-widest text-[#64748B] border-b border-slate-200">
            <th className="py-1 pr-2">Name</th>
            <th className="py-1 pr-2 text-right">Total</th>
            <th className="py-1 pr-2 text-right">Trust</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0">
              <td className="py-1.5 pr-2">{r.name}</td>
              <td className="py-1.5 pr-2 text-right text-[#4B5773]">{r.total_classified}</td>
              <td className="py-1.5 pr-2 text-right">
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                  (r.trust_score_pct ?? 0) >= 80 ? "bg-emerald-100 text-emerald-800" :
                  (r.trust_score_pct ?? 0) >= 60 ? "bg-blue-100 text-blue-800" :
                                                    "bg-slate-100 text-slate-600"}`}>
                  {r.trust_score_pct ?? 0}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReplayRow({ row }) {
  const [expanded, setExpanded] = useState(false);
  const c = OUTCOME_COLOURS[row.engine_outcome] || OUTCOME_COLOURS.needed_manager;
  return (
    <li className="px-4 py-3" data-testid="replay-row">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-[110px] text-sm text-[#0B1A36]">
          {new Date(row.work_date + "T00:00:00").toLocaleDateString("en-GB",
            { weekday: "short", day: "2-digit", month: "short" })}
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm text-[#0B1A36]">
            <strong>{row.policy_name || row.category_name || "Coverage"}</strong>
            {row.team_name ? ` · ${row.team_name}` : ""}
          </div>
          <div className="text-xs text-[#4B5773] mt-0.5">
            Verdict: <strong>{row.verdict}</strong>
            {row.shortfall ? ` · short by ${row.shortfall}` : ""}
            {" · "}engine top pick: {row.engine_top_strong_employee_name || "-"}
            {row.engine_top_strong_score ? ` (${row.engine_top_strong_score}/100)` : ""}
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${c.chip}`}>
          {OUTCOME_LABELS[row.engine_outcome]}
        </span>
        <button type="button" onClick={() => setExpanded((e) => !e)}
                className="text-xs text-[#2563EB] hover:underline"
                data-testid="replay-row-why">
          <HelpCircle className="h-3 w-3 inline mr-0.5" />
          {expanded ? "Hide" : "Why?"}
        </button>
      </div>
      {expanded && (
        <div className="mt-2 pl-[110px] text-xs text-[#0B1A36] leading-relaxed">
          {row.engine_why_text ? (
            <p>{row.engine_why_text}</p>
          ) : (
            <p className="text-[#64748B]">
              {row.engine_outcome === "needed_manager"
                ? "The engine couldn't find a candidate with the required skill, an available shift, and no leave clash for this slot. Manager judgement is the right call here."
                : "No additional rationale captured."}
            </p>
          )}
          {(row.engine_top_strong_watchouts || []).length > 0 && (
            <ul className="mt-1 list-disc list-inside text-amber-800">
              {row.engine_top_strong_watchouts.map((w, i) => (
                <li key={i}>{typeof w === "string" ? w : (w.text || w.label || JSON.stringify(w))}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}
